import { Module } from 'shared/core';
import { Injectable } from 'injection-js';

/**
 * Initialises the CyberSource Unified Checkout widget on the finalize page.
 *
 * The SDK is loaded via a <script> tag in the template (URL and SRI hash from the capture
 * context JWT, per CyberSource docs — must not be hardcoded). This module reads the capture
 * context from a data attribute, initialises the widget, and POSTs the completed payment
 * result back to our server.
 *
 * Flow (CyberSource Authorization with completeMandate + 3DS):
 *   1. VAS.UnifiedCheckout(sessionJWT) → client
 *   2. client.createCheckout() → checkout (autoProcessing defaults to true with completeMandate)
 *   3. checkout.mount('#container') → paymentResult JWT (includes 3DS + auth outcome)
 *   4. POST paymentResult to checkout_response action
 */
@Injectable()
export class UnifiedCheckoutModule extends Module {
  private submitted: boolean = false;
  private ucClient: any;
  private ucCheckout: any;

  public init(): void {
    const container = document.getElementById('unified-checkout-container');
    if (!container) return;

    const captureContext = container.dataset.captureContext;
    if (!captureContext) {
      this.showError('Payment configuration error. Please try again.');
      return;
    }

    this.launchCheckout(container, captureContext);
  }

  /** Teardown on Turbolinks navigation — destroys the UC widget and client. */
  public cleanup(): void {
    if (this.ucCheckout) {
      try { this.ucCheckout.destroy(); } catch (_) { /* ignore */ }
    }
    if (this.ucClient) {
      try { this.ucClient.destroy(); } catch (_) { /* ignore */ }
    }
  }

  /**
   * Initialises the UC SDK and mounts the payment widget. With completeMandate in the capture
   * context, UC handles 3DS authentication and authorisation internally. mount() resolves
   * with the completed payment result JWT which we POST to our server.
   */
  private async launchCheckout(container: HTMLElement, captureContext: string): Promise<void> {
    try {
      // VAS is defined globally by the SDK script loaded in the template
      const VAS = (window as any).VAS;
      if (!VAS || !VAS.UnifiedCheckout) {
        this.showError('Payment SDK not available. Please refresh and try again.');
        return;
      }

      // Step 1: Initialise the SDK with the capture context JWT
      this.ucClient = await VAS.UnifiedCheckout(captureContext);

      // Step 2: Create a checkout. autoProcessing defaults to true when completeMandate is
      // present in the capture context, so UC handles 3DS + auth within the widget.
      this.ucCheckout = await this.ucClient.createCheckout();

      // Step 3: Mount the widget into a single container (sidebar mode). With autoProcessing
      // enabled, mount() resolves with the completed payment result JWT after 3DS + auth.
      const paymentResult: string = await this.ucCheckout.mount('#unified-checkout-container');

      // Step 4: Submit the payment result to our server for verification
      this.submitPaymentResult(paymentResult, container);
    } catch (error: any) {
      if (error?.name === 'UnifiedCheckoutError') {
        console.error('UC error:', error.reason, error.message);
      } else {
        console.error('UC init error:', error);
      }
      this.showError('Unable to initialise payment form. Please refresh and try again.');
    }
  }

  /**
   * POSTs the payment result JWT to our server for verification via a hidden form.
   * Includes guards against double-submit and missing CSRF token.
   */
  private submitPaymentResult(paymentResult: string, container: HTMLElement): void {
    if (this.submitted) return;
    this.submitted = true;

    const csrfToken = $('meta[name="csrf-token"]').attr('content');
    if (!csrfToken) {
      this.submitted = false;
      this.showError('Session expired. Please refresh the page and try again.');
      return;
    }

    // Show a loading state so the customer knows we're processing
    container.innerHTML = '<p>Processing your payment, please wait...</p>';

    const checkoutResponseUrl = container.dataset.checkoutResponseUrl;
    if (!checkoutResponseUrl) {
      this.submitted = false;
      this.showError('Payment configuration error. Please try again.');
      return;
    }

    const orderId = container.dataset.orderId || '';
    const checkoutId = container.dataset.checkoutId || '';

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = checkoutResponseUrl;

    const fields: Record<string, string> = {
      payment_result: paymentResult,
      order_id: orderId,
      checkout_id: checkoutId,
      authenticity_token: csrfToken,
    };

    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
  }

  /**
   * Displays an error message to the customer in the error container.
   */
  private showError(message: string): void {
    const errorContainer = document.getElementById('unified-checkout-error');
    if (errorContainer) {
      errorContainer.textContent = message;
      errorContainer.style.display = 'block';
    }
  }
}
